/**
 * TRIPLE-A UI THEME
 * ─────────────────
 * Single source of truth for the crystal-energy visual language.
 * Every color, font size, and motion constant used by the new UI lives here.
 *
 * Reference: docs/UI_OVERHAUL_MASTERPLAN.md §2 & §5
 *
 * Rules:
 *   1. Nothing in the game uses a color not on this list.
 *   2. Nothing uses a font size outside the type scale.
 *   3. Motion eases / durations come from MOTION, not magic numbers.
 */

// ─── COLORS ──────────────────────────────────────────────────────────────────
// All values as 0xRRGGBB (Phaser numeric). String variants below for text.

export const COLORS = {
  // Core surface
  midnightDepth: 0x030816,
  abyssBlue: 0x06111f,
  deepOcean: 0x0b1d35,
  tidewater: 0x13385d,
  moonlitFoam: 0x2d5f94,

  // Metal trim
  bronzePatina: 0x5a4138,
  bronze: 0x8d6755,
  bronzeLight: 0xbf8c72,
  goldDeep: 0x88724a,
  gold: 0xceb17a,
  goldLight: 0xf4e9d0,
  goldGlow: 0xfff8ee,

  // Gems
  sapphireDeep: 0x102a66,
  sapphire: 0x2a64d8,
  sapphireLight: 0x81b0ff,
  aquaDeep: 0x0d6b88,
  aqua: 0x2cc7e8,
  aquaLight: 0x9df4ff,
  rubyCoralDeep: 0x792c30,
  rubyCoral: 0xe35b68,
  rubyCoralLight: 0xff8d98,
  pearlWhite: 0xf3fbff,
  pearlShadow: 0xaac7dc,

  // Button face (sampled from the canonical glossy pill art, ui/menu/btn_pill.png)
  buttonFace: 0x6fb5d9,      // light blue, top of the face
  buttonFaceDeep: 0x5fa6cb,  // slightly deeper blue, base of the face
  buttonRim: 0x2d7fb3,       // medium-blue outer rim
  buttonEdgeLight: 0xaccfe1, // pale-blue inner edge highlight
  buttonSheen: 0x8fc3dd,     // glossy light-blue top sheen (blue, not white)

  // State
  victoryGreen: 0x78e5b3,
  warningAmber: 0xffbf6b,
  dangerRed: 0xff6673,
  toxicGreen: 0x8ef87d,

  // Text
  textPrimary: 0xeef8ff,
  textSecondary: 0xbfd4e6,
  textTertiary: 0x6f8da6,
  textGold: 0xf4e9d0,
  textDanger: 0xff9aa4,
  textOnGold: 0x0c1726,
} as const;

/** String versions of the same palette, for Phaser Text styles. */
export const COLORS_HEX = {
  midnightDepth: '#030816',
  abyssBlue: '#06111F',
  deepOcean: '#0B1D35',
  tidewater: '#13385D',
  moonlitFoam: '#2D5F94',
  bronzePatina: '#5A4138',
  bronze: '#8D6755',
  bronzeLight: '#BF8C72',
  goldDeep: '#88724A',
  gold: '#CEB17A',
  goldLight: '#F4E9D0',
  goldGlow: '#FFF8EE',
  sapphireDeep: '#102A66',
  sapphire: '#2A64D8',
  sapphireLight: '#81B0FF',
  aquaDeep: '#0D6B88',
  aqua: '#2CC7E8',
  aquaLight: '#9DF4FF',
  rubyCoralDeep: '#792C30',
  rubyCoral: '#E35B68',
  rubyCoralLight: '#FF8D98',
  pearlWhite: '#F3FBFF',
  pearlShadow: '#AAC7DC',
  victoryGreen: '#78E5B3',
  warningAmber: '#FFBF6B',
  dangerRed: '#FF6673',
  toxicGreen: '#8EF87D',
  textPrimary: '#EEF8FF',
  textSecondary: '#BFD4E6',
  textTertiary: '#6F8DA6',
  textGold: '#F4E9D0',
  textDanger: '#FF9AA4',
  textOnGold: '#0C1726',
} as const;

// ─── TYPOGRAPHY ──────────────────────────────────────────────────────────────
// The new font stack.

export const FONTS = {
  displayLight: 'CF Asty Std Book', // lightest weight — chunky-cartoon HUD numbers
  display: 'CF Asty Std',          // titles, buttons, HUD numbers — clean modern sans-serif
  displayHeavy: 'CF Asty Std Black', // hero/title-card headlines that need extra weight
  fancy: 'Skranji Bold',           // chunky cartoon display face for win/loss titles
  fancyRegular: 'Skranji',         // lighter weight of the same family
  body: 'Cormorant Garamond',      // descriptions, tooltips, captions
  // Compatibility export so anywhere importing { FONTS.display } can use it.
} as const;

/**
 * The ONE type scale used across the entire game.
 * Don't invent new sizes — pick the right step from this list.
 */
export const TYPE = {
  hero: { fontFamily: FONTS.display, fontSize: '128px' },
  display: { fontFamily: FONTS.display, fontSize: '96px' },
  title: { fontFamily: FONTS.display, fontSize: '72px' },
  subtitle: { fontFamily: FONTS.display, fontSize: '56px' },
  section: { fontFamily: FONTS.display, fontSize: '40px' },
  button: { fontFamily: FONTS.display, fontSize: '48px' },
  label: { fontFamily: FONTS.display, fontSize: '26px' },
  scoreBig: { fontFamily: FONTS.display, fontSize: '84px' },
  hudNum: { fontFamily: FONTS.display, fontSize: '64px' },
  bodyBig: { fontFamily: FONTS.body, fontSize: '28px' },
  body: { fontFamily: FONTS.body, fontSize: '22px' },
  caption: { fontFamily: FONTS.body, fontSize: '18px' },
  tooltip: { fontFamily: FONTS.body, fontSize: '16px' },
  mono: { fontFamily: FONTS.body, fontSize: '24px' },
} as const;

/**
 * Canonical text-style presets. Use `TEXT_PRESET.title` instead of building
 * style objects inline, so the look stays consistent.
 */
export const TEXT_PRESET = {
  hero: {
    fontFamily: FONTS.display,
    fontSize: '128px',
    color: COLORS_HEX.goldLight,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 6,
    shadow: { offsetX: 0, offsetY: 6, color: COLORS_HEX.midnightDepth, blur: 14, fill: true },
  },
  display: {
    fontFamily: FONTS.display,
    fontSize: '96px',
    color: COLORS_HEX.goldLight,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 5,
    shadow: { offsetX: 0, offsetY: 4, color: COLORS_HEX.midnightDepth, blur: 10, fill: true },
  },
  title: {
    fontFamily: FONTS.display,
    fontSize: '72px',
    color: COLORS_HEX.textPrimary,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 5,
    shadow: { offsetX: 0, offsetY: 3, color: COLORS_HEX.midnightDepth, blur: 8, fill: true },
  },
  subtitle: {
    fontFamily: FONTS.display,
    fontSize: '56px',
    color: COLORS_HEX.textPrimary,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 4,
    shadow: { offsetX: 0, offsetY: 3, color: COLORS_HEX.midnightDepth, blur: 6, fill: true },
  },
  section: {
    fontFamily: FONTS.display,
    fontSize: '40px',
    color: COLORS_HEX.textPrimary,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 3,
  },
  button: {
    fontFamily: FONTS.display,
    fontSize: '48px',
    color: COLORS_HEX.goldLight,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 4,
    shadow: { offsetX: 0, offsetY: 2, color: COLORS_HEX.midnightDepth, blur: 4, fill: true },
  },
  label: {
    fontFamily: FONTS.display,
    fontSize: '26px',
    color: COLORS_HEX.textGold,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 2,
  },
  scoreBig: {
    fontFamily: FONTS.display,
    fontSize: '84px',
    color: COLORS_HEX.goldLight,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 5,
    shadow: { offsetX: 0, offsetY: 4, color: COLORS_HEX.midnightDepth, blur: 8, fill: true },
  },
  hudNum: {
    fontFamily: FONTS.display,
    fontSize: '64px',
    color: COLORS_HEX.textPrimary,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 4,
    shadow: { offsetX: 0, offsetY: 3, color: COLORS_HEX.midnightDepth, blur: 6, fill: true },
  },
  bodyBig: {
    fontFamily: FONTS.body,
    fontSize: '28px',
    color: COLORS_HEX.textPrimary,
    stroke: COLORS_HEX.abyssBlue,
    strokeThickness: 1,
  },
  body: {
    fontFamily: FONTS.body,
    fontSize: '22px',
    color: COLORS_HEX.textPrimary,
  },
  caption: {
    fontFamily: FONTS.body,
    fontSize: '18px',
    color: COLORS_HEX.textSecondary,
  },
  tooltip: {
    fontFamily: FONTS.body,
    fontSize: '16px',
    color: COLORS_HEX.textPrimary,
  },
} as const;

// ─── MOTION ──────────────────────────────────────────────────────────────────
// Canonical durations + eases. Never use raw numbers — reference from here.
// See docs/UI_OVERHAUL_MASTERPLAN.md §6.1 for the motion table.

export const MOTION = {
  dur: {
    hoverIn: 140,
    hoverOut: 100,
    pressDown: 60,
    pressUp: 180,
    modalIn: 300,
    modalOut: 220,
    scoreTick: 80,
    milestone: 200,
    heartLost: 350,
    powerupPickup: 300,
    combo: 400,
    toasterIn: 320,
    breath: 2800,
    shimmer: 1500,
    idleFloat: 2400,
  },
  ease: {
    hoverIn: 'Back.easeOut',
    hoverOut: 'Power2.easeIn',
    press: 'Power2.easeIn',
    release: 'Back.easeOut',
    modalIn: 'Back.easeOut',
    modalOut: 'Power2.easeIn',
    floaty: 'Sine.easeInOut',
    bounce: 'Back.easeOut',
    linear: 'Linear',
  },
  scale: {
    hoverUp: 1.04,
    press: 0.96,
    breath: 1.01,
    milestonePulse: 1.15,
    heartPulse: 1.4,
    modalOvershoot: 1.02,
  },
  offset: {
    hoverY: -4,
    pressY: 3,
    dropShadowY: 8,
  },
} as const;

// ─── LAYOUT / SPACING ────────────────────────────────────────────────────────

export const LAYOUT = {
  canvasW: 1920,
  canvasH: 1080,
  safeMargin: 48,
  modalPadding: 60,
  rowGap: 24,
  sectionGap: 48,
} as const;

// ─── FRAME / GEM CONSTANTS ───────────────────────────────────────────────────

export const FRAME = {
  cornerGemRadius: 8,
  metalTrimThickness: 6,
  bevelRim: 3,
  innerBevel: 2,
  dropShadowBlur: 24,
} as const;

// ─── CORNER RADIUS SCALE ─────────────────────────────────────────────────────
// Single source of truth for rounded-corner values. Use these instead of
// magic numbers so the UI keeps one consistent "softness" across every panel.
// Oceanic Overwatch calls for modern, generous rounding — not chamfers.

export const RADIUS = {
  pill: 999,   // fully rounded end-caps (energy segments, status pills)
  xs: 6,       // chips, tiny badges, inline tags
  sm: 10,      // small buttons, compact controls
  md: 14,      // nav buttons, medium cards, HUD chips
  lg: 20,      // HUD panels, bars, medium modals
  xl: 28,      // large modals (game over, stage clear, settings)
  xxl: 36,     // jumbo hero frames
} as const;

// ─── EXPORT TYPES ────────────────────────────────────────────────────────────

export type ColorKey = keyof typeof COLORS;
export type TypePresetKey = keyof typeof TEXT_PRESET;
